import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import ReactMarkdown, { type Components, type Options } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { Link } from 'react-router';
import clsx from 'clsx';
import { AppWindow, Check, Copy, Info, Lightbulb, Link2, Maximize2, TriangleAlert } from 'lucide-react';
import { GUIDE_APP_LANGUAGE, parseAppBlock, type GuideAppFrame } from '@lp/shared';
import { MARKDOWN_SCHEMA } from '../Markdown.js';

/**
 * Markdown for a guide: everything `Markdown` does, plus what a documentation
 * page needs — anchored headings that feed the "On this page" outline,
 * GitHub-style callouts (`> [!NOTE]`), a copy button on every code block, and
 * in-app links that navigate without reloading.
 *
 * The same sanitizer runs first, so nothing here widens what an author can put
 * on the page: the callout pass only reads text the sanitizer already let
 * through, and heading ids are computed here rather than taken from the author.
 *
 * A fenced block in the `app` language places one of the guide's uploaded
 * apps. The block only names the upload; the frame's address comes from the
 * API's list for this guide, so an author cannot point a frame anywhere else.
 */

export type OutlineItem = { id: string; text: string; level: 2 | 3 };

type HastNode = {
  type: string;
  tagName?: string;
  value?: string;
  properties?: Record<string, unknown>;
  children?: HastNode[];
};

function textOf(node: HastNode | undefined): string {
  if (!node) return '';
  if (node.type === 'text') return node.value ?? '';
  return (node.children ?? []).map(textOf).join('');
}

function slugOf(text: string): string {
  return (
    text
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 64) || 'section'
  );
}

/**
 * Give every h2/h3 a stable id, once per parse and in document order, so two
 * headings with the same words get distinct anchors. Runs after the sanitizer,
 * so the id is computed here and never taken from the author.
 */
function rehypeHeadingIds() {
  return (tree: HastNode) => {
    const used = new Map<string, number>();
    const walk = (node: HastNode): void => {
      for (const child of node.children ?? []) {
        if (child.type === 'element' && (child.tagName === 'h2' || child.tagName === 'h3')) {
          const text = textOf(child);
          const base = slugOf(text);
          const n = used.get(base) ?? 0;
          used.set(base, n + 1);
          child.properties = { ...child.properties, id: n === 0 ? base : `${base}-${n + 1}`, dataText: text };
        }
        walk(child);
      }
    };
    walk(tree);
  };
}

const CALLOUT = /^\s*\[!(NOTE|TIP|IMPORTANT|WARNING|CAUTION)\]\s*/i;

/** Mark blockquotes that open with `[!NOTE]` and friends, and drop the marker. */
function rehypeCallouts() {
  const walk = (node: HastNode): void => {
    for (const child of node.children ?? []) {
      if (child.type === 'element' && child.tagName === 'blockquote') {
        const first = child.children?.find((c) => c.type === 'element');
        const text = first?.tagName === 'p' ? first.children?.[0] : undefined;
        const match = text?.type === 'text' ? CALLOUT.exec(text.value ?? '') : null;
        if (text && match) {
          text.value = (text.value ?? '').slice(match[0].length);
          child.properties = { ...child.properties, dataCallout: match[1]!.toLowerCase() };
        }
      }
      walk(child);
    }
  };
  return (tree: HastNode) => walk(tree);
}

const CALLOUTS: Record<string, { label: string; icon: ReactNode; className: string; ink: string }> = {
  note: { label: 'Note', icon: <Info size={15} />, className: 'bg-project-wash border-project', ink: 'text-project' },
  important: { label: 'Important', icon: <Info size={15} />, className: 'bg-project-wash border-project', ink: 'text-project' },
  tip: { label: 'Tip', icon: <Lightbulb size={15} />, className: 'bg-accent-wash border-accent', ink: 'text-accent' },
  warning: { label: 'Warning', icon: <TriangleAlert size={15} />, className: 'bg-warn-wash border-warn', ink: 'text-warn' },
  caution: { label: 'Caution', icon: <TriangleAlert size={15} />, className: 'bg-warn-wash border-warn', ink: 'text-warn' },
};

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 1600);
    return () => window.clearTimeout(t);
  }, [copied]);
  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard?.writeText(text).then(() => setCopied(true));
      }}
      className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-control border border-line bg-surface px-2 py-1 text-xs text-ink-muted transition-colors duration-150 ease-out hover:text-ink hover:border-line-strong"
    >
      {copied ? <Check size={13} aria-hidden /> : <Copy size={13} aria-hidden />}
      <span aria-live="polite">{copied ? 'Copied' : 'Copy'}</span>
    </button>
  );
}

function Heading({ level, id, text, children }: { level: 2 | 3; id?: string; text: string; children?: ReactNode }) {
  const Tag = level === 2 ? 'h2' : 'h3';
  return (
    <Tag id={id} data-text={text} className="group relative">
      {children}
      {id ? (
        <a
          href={`#${id}`}
          aria-label={`Link to “${text}”`}
          className="ml-2 inline-flex align-middle text-ink-subtle opacity-0 transition-opacity duration-150 ease-out hover:text-brand-text focus-visible:opacity-100 group-hover:opacity-100"
        >
          <Link2 size={15} aria-hidden />
        </a>
      ) : null}
    </Tag>
  );
}

/** The guide's apps, for the `app` blocks in its body. */
const GuideAppsContext = createContext<readonly GuideAppFrame[]>([]);

function languageOf(node: HastNode | undefined): string | null {
  const code = node?.children?.find((c) => c.type === 'element' && c.tagName === 'code');
  const classes = code?.properties?.className;
  const list = Array.isArray(classes) ? classes : typeof classes === 'string' ? [classes] : [];
  const lang = list.map(String).find((c) => c.startsWith('language-'));
  return lang ? lang.slice('language-'.length) : null;
}

/**
 * One app, in a frame on the sandbox origin with scripts and nothing else:
 * an opaque origin, so no cookies, no storage of the reader's, nothing of this
 * page's to reach.
 */
function GuideAppEmbed({ source }: { source: string }) {
  const apps = useContext(GuideAppsContext);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const block = parseAppBlock(source);
  const app = block ? apps.find((a) => a.id === block.id) : undefined;

  if (!block || !app) {
    return (
      <div
        role="note"
        className="my-6 flex items-center gap-2 rounded-control border border-dashed border-line px-4 py-6 text-sm text-ink-muted"
      >
        <AppWindow size={16} aria-hidden />
        {block
          ? 'This app is not available. It may have been replaced since this page was written.'
          : 'An app block needs the id of one of this guide’s uploaded apps.'}
      </div>
    );
  }

  return (
    <figure className="my-6">
      <div className="mb-2 flex items-center justify-between gap-3">
        <figcaption className="flex min-w-0 items-center gap-1.5 text-sm font-medium text-ink">
          <AppWindow size={15} aria-hidden className="shrink-0 text-ink-muted" />
          <span className="truncate">{app.title}</span>
        </figcaption>
        <button
          type="button"
          onClick={() => void frameRef.current?.requestFullscreen?.().catch(() => undefined)}
          className="inline-flex shrink-0 items-center gap-1 rounded-control border border-line bg-surface px-2 py-1 text-xs text-ink-muted transition-colors duration-150 ease-out hover:border-line-strong hover:text-ink"
        >
          <Maximize2 size={13} aria-hidden />
          Full screen
        </button>
      </div>
      <iframe
        ref={frameRef}
        src={app.url}
        title={app.title}
        sandbox="allow-scripts"
        allowFullScreen
        referrerPolicy="no-referrer"
        loading="lazy"
        style={{ height: `${block.height}px` }}
        className="block w-full rounded-control border border-line bg-white"
      />
    </figure>
  );
}

/**
 * Module-level on purpose. Components declared inside render get a new
 * identity on every render, so React would unmount and remount every heading
 * — and the "On this page" observer would be left watching detached nodes.
 */
const COMPONENTS: Components = {
  h2: ({ node, children }) => (
    <Heading level={2} id={idOf(node)} text={textOf(node as HastNode)}>
      {children}
    </Heading>
  ),
  h3: ({ node, children }) => (
    <Heading level={3} id={idOf(node)} text={textOf(node as HastNode)}>
      {children}
    </Heading>
  ),
  a({ href, children: linkChildren, node: _node, ...rest }) {
    // Same-origin paths stay inside the app; everything else opens away
    // from it, without handing it a window reference.
    if (href && href.startsWith('/') && !href.startsWith('//')) {
      return (
        <Link to={href} {...rest}>
          {linkChildren}
        </Link>
      );
    }
    const external = !!href && /^https?:/i.test(href);
    return (
      <a href={href} {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})} {...rest}>
        {linkChildren}
      </a>
    );
  },
  img({ src, alt, node: _node, ...rest }) {
    return <img src={src} alt={alt ?? ''} loading="lazy" {...rest} />;
  },
  pre({ node, children: inner }) {
    if (languageOf(node as HastNode | undefined) === GUIDE_APP_LANGUAGE) {
      return <GuideAppEmbed source={textOf(node as HastNode)} />;
    }
    return (
      <div className="relative">
        <pre>{inner}</pre>
        <CopyButton text={textOf(node as HastNode).replace(/\n$/, '')} />
      </div>
    );
  },
  blockquote({ node, children: inner }) {
    const kind = (node as HastNode | undefined)?.properties?.dataCallout;
    const callout = typeof kind === 'string' ? CALLOUTS[kind] : undefined;
    if (!callout) return <blockquote>{inner}</blockquote>;
    return (
      <div role="note" className={clsx('rounded-control border-l-2 px-4 py-3', callout.className)}>
        <p className={clsx('eyebrow flex items-center gap-1.5', callout.ink)}>
          <span aria-hidden>{callout.icon}</span>
          {callout.label}
        </p>
        <div className="mt-1.5 text-ink [&>*+*]:mt-2">{inner}</div>
      </div>
    );
  },
};

function idOf(node: unknown): string | undefined {
  const id = (node as HastNode | undefined)?.properties?.id;
  return typeof id === 'string' ? id : undefined;
}

const REHYPE_PLUGINS: Options['rehypePlugins'] = [
  [rehypeSanitize, MARKDOWN_SCHEMA],
  rehypeHeadingIds,
  rehypeCallouts,
];
const REMARK_PLUGINS: Options['remarkPlugins'] = [remarkGfm];

const NO_APPS: readonly GuideAppFrame[] = [];

export function GuideMarkdown({
  children,
  onOutline,
  apps = NO_APPS,
  className,
}: {
  children: string;
  /** Called after each render with the article's h2/h3 headings, in order. */
  onOutline?: (items: OutlineItem[]) => void;
  /** Frames for the guide's apps, from the API. A block naming any other id shows as unavailable. */
  apps?: readonly GuideAppFrame[];
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!onOutline || !ref.current) return;
    const items = [...ref.current.querySelectorAll<HTMLHeadingElement>('h2[id], h3[id]')].map((h) => ({
      id: h.id,
      text: h.dataset.text ?? h.textContent ?? '',
      level: (h.tagName === 'H2' ? 2 : 3) as 2 | 3,
    }));
    onOutline(items);
  }, [children, onOutline]);

  return (
    <div ref={ref} className={clsx('prose-lp prose-guide', className)}>
      <GuideAppsContext.Provider value={apps}>
        <ReactMarkdown
          remarkPlugins={REMARK_PLUGINS}
          rehypePlugins={REHYPE_PLUGINS}
          components={COMPONENTS}
        >
          {children}
        </ReactMarkdown>
      </GuideAppsContext.Provider>
    </div>
  );
}
